import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  PuzzlePieceIcon,
  SparklesIcon,
  ClockIcon,
  TrophyIcon,
  AcademicCapIcon,
  PlayIcon,
  ChartBarIcon,
  ArrowsRightLeftIcon,
  GlobeAltIcon,
  CogIcon,
  LightBulbIcon,
  BoltIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  CursorArrowRaysIcon
} from '@heroicons/react/24/outline';

const MatchGameFeature: React.FC = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const pageUrl = 'https://upmindy.com/feature/match-game';
  const pageTitle = 'Match Game - Jogo de Combinação | Upmindy';
  const pageDescription = 'Aprenda brincando com o jogo de combinação do Upmindy. Pareie conceitos relacionados de forma divertida e eficaz para reforçar seu aprendizado.';
  const pageImage = 'https://upmindy.com/og-image.png';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    url: pageUrl,
    description: pageDescription,
    inLanguage: 'pt-BR',
    isPartOf: { '@type': 'WebSite', name: 'Upmindy', url: 'https://upmindy.com' }
  };

  const card = `rounded-2xl border transition-all duration-200 ${isDark ? 'border-white/10 bg-white/[0.045] hover:border-violet-300/30' : 'border-slate-200 bg-white hover:border-violet-300 hover:shadow-lg'}`;
  const title = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-600';
  const gradientText = `bg-clip-text text-transparent ${isDark ? 'bg-gradient-to-r from-violet-300 via-pink-300 to-blue-300' : 'bg-gradient-to-r from-violet-700 via-fuchsia-600 to-blue-700'}`;

  const steps = [
    { title: 'Veja as cartas', text: 'Cartas embaralhadas mostram a frente ou o verso de um flashcard.' },
    { title: 'Escolha um conceito', text: 'Clique em uma carta com um termo, pergunta ou definição.' },
    { title: 'Encontre o par', text: 'Selecione a carta com a resposta ou definição correspondente.' },
    { title: 'Acertou? Elas somem', text: 'Pares corretos desaparecem. Se errar, é só tentar de novo.' },
    { title: 'Complete o jogo', text: 'Quanto mais rápido e com menos erros, melhor sua pontuação.' }
  ];

  const types = [
    { icon: AcademicCapIcon, name: 'Baseado em Deck', text: 'Gere o jogo a partir de qualquer deck, usando frente e verso dos seus cartões.', tone: 'blue' },
    { icon: PuzzlePieceIcon, name: 'Personalizado', text: 'Crie seus próprios pares manualmente para reforçar associações específicas.', tone: 'emerald' },
    { icon: SparklesIcon, name: 'Gerado por IA', text: 'Informe um tema e receba um jogo completo e balanceado em segundos.', tone: 'violet' }
  ];

  const toneClasses: Record<string, string> = {
    blue: isDark ? 'bg-blue-500/20 text-blue-200' : 'bg-blue-100 text-blue-700',
    emerald: isDark ? 'bg-emerald-500/20 text-emerald-200' : 'bg-emerald-100 text-emerald-700',
    violet: isDark ? 'bg-violet-500/20 text-violet-200' : 'bg-violet-100 text-violet-700',
    amber: isDark ? 'bg-amber-500/20 text-amber-200' : 'bg-amber-100 text-amber-700'
  };

  const scoring = [
    { icon: ClockIcon, name: 'Tempo', text: 'Quanto mais rápido você completa, melhor a pontuação.', tone: 'blue' },
    { icon: TrophyIcon, name: 'Precisão', text: 'Menos tentativas erradas aumentam sua pontuação.', tone: 'emerald' },
    { icon: PuzzlePieceIcon, name: 'Pares', text: 'Número total de pares que você encontrou.', tone: 'violet' }
  ];

  const features = [
    { icon: PlayIcon, name: 'Múltiplas tentativas', text: 'Jogue quantas vezes quiser e supere seu recorde pessoal.' },
    { icon: ChartBarIcon, name: 'Histórico de partidas', text: 'Acompanhe sua evolução ao longo do tempo.' },
    { icon: ArrowsRightLeftIcon, name: 'Embaralhamento automático', text: 'Cada partida tem uma ordem diferente para manter o desafio.' },
    { icon: GlobeAltIcon, name: 'Jogos públicos', text: 'Compartilhe seus jogos ou jogue matches criados pela comunidade.' },
    { icon: CogIcon, name: 'Dificuldade ajustável', text: 'Escolha o número de pares (6, 8, 10 ou mais).' },
    { icon: BoltIcon, name: 'Criação automática', text: 'Jogos prontos a partir dos seus decks, sem trabalho manual.' }
  ];

  const sampleCards = ['Hello', 'Olá', 'Thanks', 'Obrigado', 'Goodbye', 'Tchau'];
  const matched = [0, 1];

  const cta = () => navigate(user ? '/matches' : '/register');

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:site_name" content="Upmindy" />
        <meta property="og:locale" content="pt_BR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className={`relative isolate overflow-hidden px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24 ${isDark ? 'bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900' : 'bg-gradient-to-br from-slate-50 via-violet-50 to-sky-100'}`}>
        <div className={`pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full blur-3xl ${isDark ? 'bg-violet-600/20' : 'bg-violet-400/20'}`} />
        <div className={`pointer-events-none absolute -bottom-40 right-0 h-96 w-96 rounded-full blur-3xl ${isDark ? 'bg-pink-500/15' : 'bg-pink-400/20'}`} />

        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <div className="text-center lg:text-left">
            <div className={`mb-6 inline-flex items-center gap-2 rounded-full border px-4 py-2 shadow-lg ${isDark ? 'border-violet-200/20 bg-violet-200/10' : 'border-violet-200 bg-white/80'}`}>
              <PuzzlePieceIcon className={`h-5 w-5 ${isDark ? 'text-pink-300' : 'text-pink-600'}`} />
              <span className={`text-sm font-semibold ${isDark ? 'text-violet-100' : 'text-violet-800'}`}>Aprendizagem gamificada</span>
            </div>
            <h1 className={`mb-6 text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl ${title}`}>
              Match Game: estude <span className={gradientText}>jogando</span>
            </h1>
            <p className={`mx-auto mb-8 max-w-xl text-base leading-relaxed sm:text-lg lg:mx-0 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Pareie conceitos relacionados em um jogo de memória educativo, enquanto desafia sua velocidade e precisão. Os jogos são criados automaticamente a partir dos seus estudos.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
              <button
                onClick={cta}
                className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-violet-500 via-fuchsia-500 to-blue-500 px-7 py-3.5 font-semibold text-white shadow-lg shadow-violet-500/25 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
              >
                <PlayIcon className="mr-2 h-5 w-5" />
                {user ? 'Jogar agora' : 'Criar conta grátis'}
              </button>
              <Link
                to="/search"
                className={`inline-flex items-center justify-center rounded-xl border px-7 py-3.5 font-medium transition-all duration-200 ${isDark ? 'border-white/20 bg-white/[0.03] text-white hover:bg-white/10' : 'border-slate-300 bg-white/70 text-slate-700 hover:border-violet-300 hover:bg-white'}`}
              >
                Explorar conteúdos
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Game preview */}
          <div className={`relative rounded-3xl border p-5 backdrop-blur-xl sm:p-6 ${isDark ? 'border-white/10 bg-white/[0.045] shadow-2xl shadow-slate-950/30' : 'border-white/80 bg-white/75 shadow-2xl shadow-slate-300/50'}`}>
            <div className="mb-4 flex items-center justify-between px-1">
              <span className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-violet-200/70' : 'text-violet-700'}`}>Partida de exemplo</span>
              <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${isDark ? 'border-white/10 bg-white/5 text-slate-300' : 'border-slate-200 bg-white text-slate-600'}`}>
                <ClockIcon className="h-3.5 w-3.5" /> 00:24
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {sampleCards.map((label, i) => {
                const done = matched.includes(i);
                const selected = i === 2;
                return (
                  <div
                    key={label}
                    className={`flex h-20 items-center justify-center rounded-xl border px-2 text-center text-sm font-semibold transition-all sm:h-24 ${
                      done
                        ? isDark ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200' : 'border-emerald-300 bg-emerald-50 text-emerald-700'
                        : selected
                          ? 'border-transparent bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/30 -translate-y-1'
                          : isDark ? 'border-white/10 bg-slate-950/40 text-slate-200' : 'border-slate-200 bg-white text-slate-800'
                    }`}
                  >
                    {done && <CheckCircleIcon className="mr-1 h-4 w-4" />}
                    {label}
                  </div>
                );
              })}
            </div>
            <p className={`mt-4 flex items-center gap-2 text-xs ${muted}`}>
              <CursorArrowRaysIcon className="h-4 w-4" /> Selecione "Thanks" e encontre o par: "Obrigado".
            </p>
          </div>
        </div>
      </section>

      {/* How to play */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 text-center">
            <h2 className={`text-3xl font-bold ${title}`}>Como jogar</h2>
            <p className={`mx-auto mt-3 max-w-2xl ${muted}`}>Regras simples, em cinco passos.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {steps.map((s, i) => (
              <div key={s.title} className={`${card} p-5`}>
                <span className="mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-sm font-bold text-white">{i + 1}</span>
                <h3 className={`mb-1 font-semibold ${title}`}>{s.title}</h3>
                <p className={`text-sm leading-relaxed ${muted}`}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types */}
      <section className={`px-4 py-16 sm:px-6 lg:px-8 ${isDark ? 'bg-slate-950/40' : 'bg-white'}`}>
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 text-center">
            <h2 className={`text-3xl font-bold ${title}`}>Tipos de Match</h2>
            <p className={`mx-auto mt-3 max-w-2xl ${muted}`}>Escolha como criar seu jogo.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {types.map(({ icon: Icon, name, text, tone }) => (
              <div key={name} className={`${card} p-6`}>
                <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl ${toneClasses[tone]}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className={`mb-2 text-lg font-semibold ${title}`}>{name}</h3>
                <p className={`text-sm leading-relaxed ${muted}`}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scoring */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="mb-10 text-center">
            <h2 className={`text-3xl font-bold ${title}`}>Sistema de pontuação</h2>
            <p className={`mx-auto mt-3 max-w-2xl ${muted}`}>Seu desempenho é avaliado por três métricas.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {scoring.map(({ icon: Icon, name, text, tone }) => (
              <div key={name} className={`${card} p-6 text-center`}>
                <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${toneClasses[tone]}`}>
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className={`mb-1 font-semibold ${title}`}>{name}</h3>
                <p className={`text-sm ${muted}`}>{text}</p>
              </div>
            ))}
          </div>
          <div className={`mt-6 flex items-start gap-3 rounded-2xl border p-5 ${isDark ? 'border-amber-400/20 bg-amber-500/10' : 'border-amber-200 bg-amber-50'}`}>
            <TrophyIcon className={`h-6 w-6 shrink-0 ${isDark ? 'text-amber-300' : 'text-amber-600'}`} />
            <p className={`text-sm ${isDark ? 'text-amber-100' : 'text-amber-900'}`}>
              <strong>Pontuação perfeita:</strong> complete o jogo sem erros no menor tempo possível para alcançar 100% e bater seu próprio recorde.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className={`px-4 py-16 sm:px-6 lg:px-8 ${isDark ? 'bg-slate-950/40' : 'bg-white'}`}>
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 text-center">
            <h2 className={`text-3xl font-bold ${title}`}>Recursos do Match</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map(({ icon: Icon, name, text }) => (
              <div key={name} className={`${card} flex items-start gap-4 p-5`}>
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${toneClasses.violet}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className={`mb-1 font-semibold ${title}`}>{name}</h3>
                  <p className={`text-sm leading-relaxed ${muted}`}>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + usage */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2">
          {[
            { heading: 'Benefícios para o aprendizado', items: ['Melhora a memória associativa', 'Desenvolve reconhecimento de padrões', 'Aumenta a velocidade de processamento', 'Reduz a ansiedade de estudo', 'Aumenta engajamento e motivação'] },
            { heading: 'Ideal para', items: ['Vocabulário e definições', 'Países e capitais', 'Fórmulas e aplicações', 'Termos técnicos e significados', 'Aquecimento antes de estudar'] }
          ].map((col) => (
            <div key={col.heading} className={`${card} p-6`}>
              <h3 className={`mb-4 text-xl font-semibold ${title}`}>{col.heading}</h3>
              <ul className="space-y-3">
                {col.items.map((item) => (
                  <li key={item} className={`flex items-center gap-3 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-blue-600 p-8 text-center text-white shadow-2xl shadow-violet-500/20 sm:p-12">
          <LightBulbIcon className="mx-auto mb-4 h-10 w-10 text-amber-300" />
          <h2 className="mb-3 text-2xl font-bold sm:text-3xl">Match é mais que diversão</h2>
          <p className="mx-auto mb-8 max-w-2xl text-white/85">
            O jogo treina memória de trabalho, atenção e velocidade de processamento. Combine com flashcards e quizzes para um estudo completo.
          </p>
          <button
            onClick={cta}
            className="inline-flex items-center rounded-xl bg-white px-7 py-3.5 font-semibold text-violet-700 shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl"
          >
            <PlayIcon className="mr-2 h-5 w-5" />
            {user ? 'Jogar agora' : 'Começar grátis'}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MatchGameFeature;
